export type LiveActivityAppState = 'active' | 'background' | 'inactive' | 'unknown' | 'extension';

export type LiveActivitySession = {
  activeId: string | null;
  lastPayload: string;
};

export type LiveActivityInstanceRef = {
  id: string;
};

export type LiveActivityGateway<T> = {
  getInstances: () => LiveActivityInstanceRef[];
  start: (snapshot: T) => LiveActivityInstanceRef;
  update: (id: string, snapshot: T) => Promise<void> | void;
  end: (id: string, policy: 'default' | 'immediate') => Promise<void> | void;
};

export type LiveActivitySyncAction = 'hold' | 'push' | 'clear';

const emptySession = (): LiveActivitySession => ({ activeId: null, lastPayload: '' });

/**
 * Boot and refetch can look like "signed out" or "no snapshot" for a tick.
 * Clearing then starting again leaves Apple's ended-but-still-visible card
 * next to the new one. Hold until auth and week data have actually settled.
 */
export function liveActivitySyncAction(input: {
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  snapshotReady: boolean;
  snapshot: unknown;
}): LiveActivitySyncAction {
  if (input.isAuthLoading) return 'hold';
  if (!input.isAuthenticated) return 'clear';
  if (!input.snapshotReady) return 'hold';
  return input.snapshot ? 'push' : 'clear';
}

/**
 * Keeps a single Live Activity in sync with a snapshot.
 * ActivityKit only allows start() while the app is foregrounded. Updates of an
 * activity that is already on the lock screen are allowed in the background.
 * Ending a duplicate must not block the update of the one that remains.
 *
 * Always dismiss extras immediately. Apple's default policy leaves an ended
 * activity on the lock screen for hours, which looks like a second card.
 */
export async function applyLiveActivityUpdate<T>(input: {
  session: LiveActivitySession;
  gateway: LiveActivityGateway<T>;
  snapshot: T | null;
  appState: LiveActivityAppState | string;
}): Promise<LiveActivitySession> {
  const { gateway, snapshot, appState } = input;
  const { activeId, lastPayload } = input.session;

  if (!snapshot) {
    const ids = new Set<string>();
    if (activeId) ids.add(activeId);
    gateway.getInstances().forEach((instance) => ids.add(instance.id));
    for (const id of ids) {
      try {
        await gateway.end(id, 'immediate');
      } catch {
        // Already gone.
      }
    }
    return emptySession();
  }

  const payload = JSON.stringify(snapshot);
  const instances = gateway.getInstances();
  const known = activeId ? instances.find((instance) => instance.id === activeId) : undefined;
  const keeper = known ?? instances[0];
  const extras = instances.filter((instance) => instance.id !== keeper?.id);

  if (payload === lastPayload && keeper?.id === activeId && extras.length === 0) {
    return input.session;
  }

  for (const extra of extras) {
    try {
      await gateway.end(extra.id, 'immediate');
    } catch {
      // A duplicate that already ended should not block the visible activity.
    }
  }

  if (!keeper) {
    if (appState !== 'active') return { activeId: null, lastPayload };
    const started = gateway.start(snapshot);
    return { activeId: started.id, lastPayload: payload };
  }

  if (payload !== lastPayload || keeper.id !== activeId) {
    await gateway.update(keeper.id, snapshot);
  }
  return { activeId: keeper.id, lastPayload: payload };
}
